import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Divider,
  Autocomplete,
} from "@mui/material";
import { FileDownload, Print } from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import * as xlsx from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DynamicTable from "../../components/lab-dynamic-table"; // Ensure this path is correct
import DateRangeSelector from "../DateRangeSelector";
import { formatCurrency } from "../../utils/numberUtils";

// Doctor interface
interface Doctor {
  _id: string;
  fullName: string;
}

// Nested interface for patient information, matching the API response
interface PatientInfo {
  patientName: string;
  mrn: number;
  guardiansName: string;
  phonNumber: string;
  gender: string;
}

// Main interface for the summary data, matching the API response
interface IncomeSummary {
  _id: string;
  invoiceId: number;
  patientInfo: PatientInfo;
  grandTotal: number;
  paidAmount: number;
  drShares: number;
  hospitalShare: number;
  doctorName?: string;
}

export const IncomeSummaryTab: React.FC = () => {
  const today = new Date().toISOString().split("T")[0];

  const [summaryData, setSummaryData] = useState<IncomeSummary[]>([]);
  const [loading, setLoading] = useState(false);

  // State for doctor filter
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string>("");

  // State for date filtering
  const [dateRange, setDateRange] = useState("Today");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  // State for pagination
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(100);
  const [totalCount, setTotalCount] = useState<number>(0);

  // State for sorting
  const [sort, setSort] = useState<string>("descending");

  // Fetch doctors list
  const fetchDoctors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/users/getAllSharerDoctors`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const fetchedDoc = response.data?.data || [];
      const uniqueDoc = Array.from(
        new Map(fetchedDoc.map((item: any) => [item.fullName, item])).values()
      );
      setDoctors(uniqueDoc as Doctor[]);
    } catch (error) {
      console.error("Error fetching doctors:", error);
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
    setPage(0); // Reset to the first page when the date range changes
  };

  const fetchSummaryData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/income/getSummary`,
        {
          params: {
            fromDate,
            toDate,
            pageNo: page + 1, // API is 1-based, component is 0-based
            count: rowsPerPage,
            sort,
            ...(selectedDoctorId && { doctorId: selectedDoctorId }),
          },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (response.data.isSuccess) {
        // Correctly access the data array and totalCount from the response
        setSummaryData(response.data.data || []);
        setTotalCount(response.data.totalCount || 0);
      } else {
        toast.error(response.data.message || "Failed to fetch summary");
      }
    } catch (err: any) {
      toast.error(err.message || "Error fetching summary data");
      setSummaryData([]);
    } finally {
      setLoading(false);
    }
  };

  // Refetch data when dependencies like date, page, or sort order change
  useEffect(() => {
    fetchSummaryData();
  }, [fromDate, toDate, page, rowsPerPage, sort, selectedDoctorId]);

  // Handlers for pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Note: Exports only the currently visible page. For a full export, a separate API call without pagination would be needed.
  const handleExcelExport = () => {
    if (!summaryData || summaryData?.length === 0) {
      toast.warn("No data to export.");
      return;
    }
    const worksheetData = summaryData?.map(row => ({
      "Invoice ID": row.invoiceId,
      MRN: row.patientInfo.mrn,
      "Patient Name": row.patientInfo.patientName,
      "Guardian's Name": row.patientInfo.guardiansName,
      "Doctor Name": row.doctorName || "N/A",
      "Grand Total": row.grandTotal,
      "Paid Amount": row.paidAmount,
      "Doctor Share": row.drShares,
      "Hospital Share": row.hospitalShare,
    }));

    // Calculate totals
    const totalGrand = summaryData.reduce((sum, item) => sum + (Number(item?.grandTotal) || 0), 0);
    const totalPaidAmount = summaryData.reduce((sum, item) => sum + (Number(item?.paidAmount) || 0), 0);
    const totalDoctorShare = summaryData.reduce((sum, item) => sum + (Number(item?.drShares) || 0), 0);
    const totalHospitalShare = summaryData.reduce((sum, item) => sum + (Number(item?.hospitalShare) || 0), 0);

    // Add total row
    worksheetData.push({
      "Invoice ID": "" as any,
      MRN: "" as any,
      "Patient Name": "TOTAL",
      "Guardian's Name": "",
      "Doctor Name": "",
      "Grand Total": totalGrand,
      "Paid Amount": totalPaidAmount,
      "Doctor Share": totalDoctorShare,
      "Hospital Share": totalHospitalShare,
    });

    const worksheet = xlsx.utils.json_to_sheet(worksheetData);
    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "IncomeSummary");
    xlsx.writeFile(workbook, `Income_Summary_${fromDate}_to_${toDate}.xlsx`);
  };

  const handlePrint = () => {
    if (!summaryData || summaryData.length === 0) {
      toast.warn("No data to print.");
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 8;
    let yPosition = margin;

    // Professional header with minimal, clean design
    doc
      .setFontSize(10)
      .setFont("helvetica", "bold")
      .setTextColor(25, 55, 85)
      .text("INCOME SUMMARY REPORT", pageWidth / 2, yPosition, {
        align: "center",
      });
    
    yPosition += 6;
    doc
      .setDrawColor(25, 55, 85)
      .setLineWidth(0.5)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    // Subtle metadata section with smaller font
    yPosition += 3;
    doc
      .setFontSize(6.5)
      .setFont("helvetica", "normal")
      .setTextColor(80, 80, 80)
      .text(
        `Report Period: ${fromDate} to ${toDate} | Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
        pageWidth / 2,
        yPosition,
        { align: "center" }
      );

    yPosition += 6;

    const head = [
      [
        "INVOICE ID",
        "MRN",
        "PATIENT NAME",
        "DOCTOR NAME",
        "GRAND TOTAL",
        "PAID AMOUNT",
        "DOCTOR SHARE",
        "HOSPITAL SHARE",
      ],
    ];

    const body = summaryData?.map(row => [
      row.invoiceId,
      row.patientInfo.mrn,
      row.patientInfo.patientName,
      row.doctorName || "N/A",
      formatCurrency(row.grandTotal || 0),
      formatCurrency(row.paidAmount || 0),
      formatCurrency(row.drShares || 0),
      formatCurrency(row.hospitalShare || 0),
    ]);

    // Calculate totals
    const totalGrand = summaryData.reduce((sum, item) => sum + (Number(item?.grandTotal) || 0), 0);
    const totalPaidAmount = summaryData.reduce((sum, item) => sum + (Number(item?.paidAmount) || 0), 0);
    const totalDoctorShare = summaryData.reduce((sum, item) => sum + (Number(item?.drShares) || 0), 0);
    const totalHospitalShare = summaryData.reduce((sum, item) => sum + (Number(item?.hospitalShare) || 0), 0);

    body.push([
      "",
      "",
      "TOTAL",
      "",
      formatCurrency(totalGrand),
      formatCurrency(totalPaidAmount),
      formatCurrency(totalDoctorShare),
      formatCurrency(totalHospitalShare),
    ]);

    // Clean, professional table styling
    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      headStyles: { 
        fillColor: [25, 55, 85],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 6,
        halign: 'left',
        valign: 'middle',
        cellPadding: 2
      },
      bodyStyles: {
        fontSize: 5.5,
        textColor: [40, 40, 40],
        cellPadding: 1.8
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "left" },
        2: { halign: "left" },
        3: { halign: "left" },
        4: { halign: "left" },
        5: { halign: "left" },
        6: { halign: "left" },
        7: { halign: "left" },
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      margin: { top: margin, right: margin, bottom: margin, left: margin },
      didParseCell: function(data) {
        // Professional total row with premium styling
        if (data.row.index === body.length - 1) {
          data.cell.styles.fillColor = [25, 55, 85];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 5.8;
          data.cell.styles.lineColor = [200, 200, 200];
          data.cell.styles.lineWidth = 0.3;
          (data.cell.styles as any).cellPadding = { top: 2.5, right: 2, bottom: 2.5, left: 2 };
        }
      },
      didDrawPage: function(data) {
        // Add subtle footer with page number
        const pageSize = doc.internal.pageSize;
        const pageCount = doc.getNumberOfPages();
        const pageNum = data.pageNumber;
        
        doc.setFontSize(5);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${pageNum} of ${pageCount}`,
          pageSize.getWidth() / 2,
          pageSize.getHeight() - 4,
          { align: "center" }
        );
      }
    });

    doc.autoPrint();
    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);
    
    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      }
      window.removeEventListener("focus", cleanup);
    };
    
    window.addEventListener("focus", cleanup);
    iframe.src = blobUrl;
    
    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (win) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Error triggering print dialog:", e);
        cleanup();
      }
    };
    
    // Fallback print trigger
    setTimeout(() => {
      try {
        const win = iframe.contentWindow;
        if (win && document.body.contains(iframe)) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Fallback print attempt failed:", e);
        cleanup();
      }
    }, 1000);
  };


  
  // Prepare data for the DynamicTable component
  const tableData = summaryData?.map(item => [
    item?.invoiceId,
    item?.patientInfo?.mrn,
    item?.patientInfo?.patientName,
    item?.doctorName || "N/A",
    formatCurrency(item?.grandTotal || 0),
    formatCurrency(item?.paidAmount || 0),
    formatCurrency(item?.drShares || 0),
    formatCurrency(item?.hospitalShare || 0),
  ]);

  // Calculate totals for Grand Total, Paid Amount, Doctor Share, and Hospital Share
  const totalGrand = summaryData.reduce(
    (sum, item) => sum + (item.grandTotal || 0),
    0
  );
  const totalPaidAmount = summaryData.reduce(
    (sum, item) => sum + (item.paidAmount || 0),
    0
  );
  const totalDoctorShare = summaryData.reduce(
    (sum, item) => sum + (item.drShares || 0),
    0
  );
  const totalHospitalShare = summaryData.reduce(
    (sum, item) => sum + (item.hospitalShare || 0),
    0
  );

  // Append totals row to the table data
  const tableDataWithTotals = [
    ...tableData,
    [
      "", // Empty Invoice ID
      "",
      "", // Empty MRN
      <strong>Totals</strong>, // Label for totals row
    // Empty Doctor Name
      <strong>{formatCurrency(totalGrand)}</strong>,
      <strong>{formatCurrency(totalPaidAmount)}</strong>,
      <strong>{formatCurrency(totalDoctorShare)}</strong>,
      <strong>{formatCurrency(totalHospitalShare)}</strong>,
    ],
  ];

  // Reusable button styles
  const gradientButtonStyle = {
    minWidth: { xs: "auto", md: "80px" },
    fontSize: { xs: "12px", md: "12px" },
    fontWeight: "600",
    textTransform: "none",
    borderRadius: "8px",
    background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
    color: "#ffffff",
    boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
      boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
      transform: "translateY(-1px)",
    },
  };

  return (
    <Box sx={{ background: "#fff", borderRadius: "12px" }}>
      {/* --- CHANGE 1: REMOVED THE HEADER AND DIVIDER THAT WERE HERE --- */}

      <Box
        sx={{
          p: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          {/* --- CHANGE 2: APPLIED GRADIENT STYLES TO BUTTONS --- */}
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownload />}
            onClick={handleExcelExport}
            sx={gradientButtonStyle}
          >
            Excel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={gradientButtonStyle}
          >
            Print
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Box sx={{ minWidth: 200 }}>
            <Typography   sx={{
            fontSize: "12px",
            fontWeight: "600",
            color:"#000" ,
          }}>
              Filter by Doctor
            </Typography>
            <Autocomplete
              size="small"
              className="custom-input"
              fullWidth
              options={doctors || []}
              value={doctors.find((doc) => doc._id === selectedDoctorId) || null}
              onChange={(event, newValue) => {
                setSelectedDoctorId(newValue?._id || "");
                setPage(0);
              }}
              getOptionLabel={(option) => option.fullName || ""}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="All Doctors"
                  className="custom-input"
                  variant="outlined"
                  size="small"
                  // sx={{
                  //   borderRadius: "8px",
                  //   "& .MuiOutlinedInput-root": {
                  //     "& fieldset": {
                  //       borderColor: "#d0d0d0",
                  //     },
                  //     "&:hover fieldset": {
                  //       borderColor: "#1976d2",
                  //     },
                  //   },
                  // }}
                />
              )}
            />
          </Box>
          <DateRangeSelector
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={handleDateRangeChange}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
        </Box>
      </Box>

      <Box
        sx={{
          p: 1,
          maxHeight: "60vh",
          overflowY: "hidden",
        }}
      >
        <DynamicTable
          headers={[
            "Invoice ID",
            "MRN",
            "Patient Name",
            "Doctor Name",
            "Grand Total",
            "Paid Amount",
            "Doctor Share",
            "Hospital Share",
          ]}
          data={tableDataWithTotals} // Use the updated table data with totals row
          loading={loading}
          // Pagination props passed to the table component
          height={"60vh"}
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={totalCount}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
};
